import { useCallback, useMemo, useState } from 'react';

import { useDisclosure } from '@mantine/hooks';
import type { TableProps } from 'components/Table';
import useDuplicateWorkoutTemplate from 'hooks/workoutTemplate/useDuplicateWorkoutTemplate';
import useWorkoutTemplate from 'hooks/workoutTemplate/useWorkoutTemplate';
import useWorkoutTemplates from 'hooks/workoutTemplate/useWorkoutTemplates';
import { useNavigate } from 'react-router-dom';
import { WorkoutTemplateList } from 'types';
import notificationHandler from 'utils/notifications';

import type { WorkoutTemplatesColumnsInput } from './columns/useWorkoutTemplatesColumns';
import type { WorkoutTemplatesTableContainerProps } from './types';
import { buildPreviewData } from './utils/buildPreviewData';
import { getOrdering } from './utils/getOrdering';
import type { WorkoutTemplatePreviewData } from '../ModalWorkoutTemplatePreview';

type TablePropsWithoutData = Omit<TableProps, 'tableData' | 'columns'>;

interface WorkoutTemplatesTableContainerResult {
  tableData: WorkoutTemplateList[];
  columnsInput: WorkoutTemplatesColumnsInput;
  tableProps: TablePropsWithoutData;
  deleteModal: {
    opened: boolean;
    template: WorkoutTemplateList | null;
    onClose: () => void;
  };
  previewModal: {
    opened: boolean;
    template: WorkoutTemplatePreviewData | null;
    onClose: () => void;
  };
}

export function useWorkoutTemplatesTable(
  props: WorkoutTemplatesTableContainerProps,
): WorkoutTemplatesTableContainerResult {
  const { permission = true, search = '', sortOption = 'default' } = props;
  const navigate = useNavigate();
  const [ascending, setAscending] = useState(true);
  const [templateToDelete, setTemplateToDelete] = useState<WorkoutTemplateList | null>(null);
  const [deleteModalOpened, { open: openDeleteModal, close: closeDeleteModal }] = useDisclosure(false);
  const [previewTemplateId, setPreviewTemplateId] = useState<string | null>(null);
  const [previewModalOpened, { open: openPreviewModal, close: closePreviewModal }] = useDisclosure(false);

  const ordering = useMemo(() => getOrdering(sortOption, ascending), [sortOption, ascending]);

  const { data, fetchNextPage, hasNextPage, isSuccess, isError, isLoading, isFetchingNextPage } =
    useWorkoutTemplates({
      search: search || undefined,
      ordering,
      limit: '20',
    });

  const duplicateMutation = useDuplicateWorkoutTemplate();
  const { successNotification, errorNotification } = notificationHandler();
  const { data: previewTemplateData } = useWorkoutTemplate(previewTemplateId || undefined);

  const tableData = useMemo(() => data?.pages.flatMap(page => page.data.results) ?? [], [data]);

  const previewData: WorkoutTemplatePreviewData | null = useMemo(() => {
    if (!previewTemplateData?.data) return null;
    return buildPreviewData(previewTemplateData.data);
  }, [previewTemplateData]);

  const handleHeaderClick = useCallback(
    (header: string): void => {
      if (sortOption === 'name' && header === 'name') {
        setAscending(a => !a);
      }
    },
    [sortOption],
  );

  const handleEdit = useCallback(
    (template: WorkoutTemplateList) => {
      navigate(`/workouts/library/templates/edit/${template.workoutTemplateId}`);
    },
    [navigate],
  );

  const handlePreview = useCallback(
    (template: WorkoutTemplateList) => {
      setPreviewTemplateId(template.workoutTemplateId);
      openPreviewModal();
    },
    [openPreviewModal],
  );

  const handleClosePreview = useCallback(() => {
    closePreviewModal();
    setPreviewTemplateId(null);
  }, [closePreviewModal]);

  const handleDelete = useCallback(
    (template: WorkoutTemplateList) => {
      setTemplateToDelete(template);
      openDeleteModal();
    },
    [openDeleteModal],
  );

  const handleCloseDeleteModal = useCallback(() => {
    setTemplateToDelete(null);
    closeDeleteModal();
  }, [closeDeleteModal]);

  const handleDuplicate = useCallback(
    (template: WorkoutTemplateList) => {
      duplicateMutation.mutate(template.workoutTemplateId, {
        onSuccess: () => {
          successNotification('Template duplicated', `"${template.name}" has been duplicated successfully.`);
        },
        onError: error => {
          const message = error.response?.data?.message ?? 'Failed to duplicate workout template';
          errorNotification('Duplicate failed', message);
        },
      });
    },
    [duplicateMutation, successNotification, errorNotification],
  );

  return {
    tableData,
    columnsInput: {
      permission,
      handlePreview,
      handleEdit,
      handleDuplicate,
      handleDelete,
    },
    tableProps: {
      isSuccess,
      isLoading,
      isError,
      isFetchingNextPage,
      hasNextPage: !!hasNextPage,
      fetchNextPage,
      clickableHeaders: ['name'],
      handleHeaderClick,
      ordering: sortOption === 'name' ? 'name' : 'created_at',
      ascending,
      margin: 12,
      bgc: 'white' as const,
    },
    deleteModal: {
      opened: deleteModalOpened,
      template: templateToDelete,
      onClose: handleCloseDeleteModal,
    },
    previewModal: {
      opened: previewModalOpened,
      template: previewData,
      onClose: handleClosePreview,
    },
  };
}
